import { h, card, apiClient } from './_common.js';

// Buffer en memoria para sparklines (no se persiste)
const HISTORY_KEY = '__sys_history__';
const MAX_POINTS = 60; // ~30 min si refresca cada 30s
function getHistory() {
  if (!window[HISTORY_KEY]) window[HISTORY_KEY] = { ram: [], cpu: [], disk: [], ts: [] };
  return window[HISTORY_KEY];
}
function pushHistory(stats) {
  const h = getHistory();
  h.ram.push(stats.memory?.usedPct ?? 0);
  h.cpu.push(stats.cpu?.loadPct ?? 0);
  h.disk.push(stats.disk?.usedPct ?? 0);
  h.ts.push(Date.now());
  while (h.ram.length > MAX_POINTS) {
    h.ram.shift(); h.cpu.shift(); h.disk.shift(); h.ts.shift();
  }
}

let autoRefreshTimer = null;

export async function renderSistema(root) {
  root.innerHTML = '';
  // Limpiar timer previo si re-renderiza
  if (autoRefreshTimer) { clearInterval(autoRefreshTimer); autoRefreshTimer = null; }

  const wrap = h('div', { class: 'animate-fade-in' }, [
    h('div', { class: 'flex items-center justify-between mb-6 flex-wrap gap-3' }, [
      h('div', {}, [
        h('h2', { class: 'font-display text-3xl font-bold' }, '🖥️ Sistema'),
        h('p', { class: 'text-ink-300' }, 'VPS Hetzner CPX22 · Falkenstein · Live.'),
      ]),
      h('div', { class: 'flex items-center gap-2' }, [
        h('span', { id: 'sys-last-update', class: 'text-xs text-ink-400' }, ''),
        h('button', { class: 'text-xs text-gold-400 hover:text-gold-300 px-2 py-1 border border-base-700 rounded', onclick: () => doRefresh(wrap) }, '⟳ Refrescar'),
      ]),
    ]),
    h('div', { id: 'sys-stats', class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-6' }),
    h('div', { id: 'sys-services', class: 'mb-6' }),
    h('div', { id: 'sys-processes' }),
  ]);
  root.appendChild(wrap);

  await doRefresh(wrap);
  // Auto refresh cada 30s
  autoRefreshTimer = setInterval(() => doRefresh(wrap), 30_000);
  // Limpiar timer si el wrap se desconecta
  const observer = new MutationObserver(() => {
    if (!document.body.contains(wrap)) {
      clearInterval(autoRefreshTimer);
      autoRefreshTimer = null;
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

async function doRefresh(wrap) {
  const [stats, services] = await Promise.all([
    apiClient.get('/api/v1/system/stats').catch(() => null),
    apiClient.get('/api/v1/system/services').catch(() => null),
  ]);
  const statsEl = wrap.querySelector('#sys-stats');
  if (!stats) {
    statsEl.innerHTML = '';
    statsEl.appendChild(h('p', { class: 'text-red-400 col-span-4' }, 'No se pudieron cargar las stats.'));
    return;
  }
  pushHistory(stats);
  const hist = getHistory();

  const tsEl = wrap.querySelector('#sys-last-update');
  if (tsEl) tsEl.textContent = `Última: ${new Date().toLocaleTimeString('es-ES')}`;

  // ===== TARJETAS PRINCIPALES =====
  statsEl.innerHTML = '';
  statsEl.appendChild(metricCard('RAM', stats.memory.usedPct, `${fmtGB(stats.memory.usedBytes)} / ${fmtGB(stats.memory.totalBytes)}`, colorByPct(stats.memory.usedPct), hist.ram));
  statsEl.appendChild(metricCard('Disco', stats.disk?.usedPct || 0, stats.disk ? `${fmtGB(stats.disk.usedBytes)} / ${fmtGB(stats.disk.totalBytes)} · libre ${fmtGB(stats.disk.freeBytes)}` : '—', colorByPct(stats.disk?.usedPct || 0), hist.disk));
  statsEl.appendChild(metricCard('CPU', stats.cpu.loadPct, `Load ${stats.cpu.loadAvg['1m']?.toFixed(2)} · ${stats.cpu.count} cores`, colorByPct(stats.cpu.loadPct), hist.cpu));
  statsEl.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('div', { class: 'text-xs uppercase tracking-wider text-ink-300 mb-1' }, 'Uptime'),
    h('div', { class: 'text-2xl font-display font-bold text-gold-300' }, fmtUptime(stats.uptimeSec)),
    h('div', { class: 'text-xs text-ink-300 mt-1' }, `${stats.hostname} · ${stats.platform}/${stats.arch}`),
  ]));

  // ===== SERVICIOS =====
  const svcSec = wrap.querySelector('#sys-services');
  svcSec.innerHTML = '';
  svcSec.appendChild(card('Servicios systemd', { subtitle: 'Estado de los procesos clave' }));
  if (services && services.services) {
    const list = h('div', { class: '-mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 pl-5' });
    for (const [name, s] of Object.entries(services.services)) {
      list.appendChild(h('div', { class: 'bg-base-800 border border-base-700 rounded-lg p-3' }, [
        h('div', { class: 'flex items-center gap-2 mb-1' }, [
          h('span', { class: s.active ? 'w-2 h-2 rounded-full bg-emerald-400' : 'w-2 h-2 rounded-full bg-red-400' }, ''),
          h('span', { class: 'font-mono text-sm' }, name),
        ]),
        h('div', { class: 'text-xs text-ink-300' }, s.active ? 'activo' : 'parado'),
        s.memBytes ? h('div', { class: 'text-xs text-ink-300' }, `${fmtMB(s.memBytes)} RAM`) : null,
      ]));
    }
    svcSec.appendChild(list);
  }

  // ===== TOP PROCESOS =====
  const procSec = wrap.querySelector('#sys-processes');
  procSec.innerHTML = '';
  procSec.appendChild(card('Top procesos por CPU', { subtitle: 'Snapshot ahora mismo' }));
  if (stats.topProcesses && stats.topProcesses.length > 0) {
    const table = h('div', { class: '-mt-3 pl-5' });
    table.appendChild(h('div', { class: 'grid grid-cols-12 text-xs uppercase tracking-wider text-ink-400 py-1 border-b border-base-700' }, [
      h('div', { class: 'col-span-1' }, 'PID'),
      h('div', { class: 'col-span-7' }, 'Proceso'),
      h('div', { class: 'col-span-2 text-right' }, 'CPU%'),
      h('div', { class: 'col-span-2 text-right' }, 'MEM%'),
    ]));
    stats.topProcesses.forEach((p) => table.appendChild(
      h('div', { class: 'grid grid-cols-12 text-sm py-1.5 border-b border-base-700/40' }, [
        h('div', { class: 'col-span-1 text-ink-400' }, String(p.pid)),
        h('div', { class: 'col-span-7 font-mono text-xs truncate' }, p.command),
        h('div', { class: 'col-span-2 text-right text-gold-300' }, p.cpuPct.toFixed(1)),
        h('div', { class: 'col-span-2 text-right text-ink-200' }, p.memPct.toFixed(1)),
      ]),
    ));
    procSec.appendChild(table);
  }

  // ===== BD =====
  if (stats.database?.sizeBytes != null) {
    procSec.appendChild(h('div', { class: 'mt-4 text-xs text-ink-400 pl-5' },
      `📦 Tamaño BD CARLOS OS: ${fmtMB(stats.database.sizeBytes)}`,
    ));
  }

  // ===== MAC (Fase 5 cuando esté Tailscale) =====
  procSec.appendChild(h('div', { class: 'mt-6 bg-base-800/30 border border-dashed border-base-600 rounded-xl p-4 text-center' }, [
    h('div', { class: 'text-3xl mb-2' }, '🖥️'),
    h('h4', { class: 'font-semibold' }, 'Stats de tu Mac'),
    h('p', { class: 'text-sm text-ink-300 mt-1' }, 'Disponibles cuando montemos Tailscale + agente Mac en Fase 5.'),
  ]));
}

// ============================================================
// Tarjeta de métrica con sparkline
// ============================================================
function metricCard(label, pct, sub, color, history) {
  const colorClass = color === 'red' ? 'from-red-400 to-red-500' : color === 'gold' ? 'from-gold-400 to-gold-500' : 'from-emerald-400 to-emerald-500';
  const strokeColor = color === 'red' ? '#f87171' : color === 'gold' ? '#E8A020' : '#34d399';

  const canvas = h('canvas', { class: 'w-full', style: 'height: 28px; display: block;' });

  const card = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('div', { class: 'flex items-center justify-between mb-1' }, [
      h('div', { class: 'text-xs uppercase tracking-wider text-ink-300' }, label),
      h('div', { class: 'text-[10px] text-ink-400' }, `n=${history.length}`),
    ]),
    h('div', { class: 'text-2xl font-display font-bold' }, `${Math.round(pct)}%`),
    h('div', { class: 'mt-2 h-2 bg-base-700 rounded-full overflow-hidden' }, [
      h('div', { class: `h-full bg-gradient-to-r ${colorClass} transition-all`, style: `width: ${Math.min(pct, 100)}%` }),
    ]),
    h('div', { class: 'mt-2' }, canvas),
    h('div', { class: 'text-xs text-ink-300 mt-2' }, sub),
  ]);

  // Render sparkline después de añadir al DOM (necesita dimensiones)
  setTimeout(() => drawSparkline(canvas, history, strokeColor), 0);

  return card;
}

function drawSparkline(canvas, data, strokeColor) {
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth || 200;
  const h = 28;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, w, h);

  if (data.length < 2) {
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.font = '10px sans-serif';
    ctx.fillText('recogiendo datos…', 2, h / 2 + 3);
    return;
  }

  // Grid horizontal sutil
  ctx.strokeStyle = 'rgba(255,255,255,0.05)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, h / 2); ctx.lineTo(w, h / 2);
  ctx.stroke();

  // Línea (0..100%)
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  data.forEach((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / 100) * h;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  });
  ctx.stroke();

  // Área debajo (degradado sutil)
  ctx.lineTo(w, h);
  ctx.lineTo(0, h);
  ctx.closePath();
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, strokeColor + '33');
  grad.addColorStop(1, strokeColor + '00');
  ctx.fillStyle = grad;
  ctx.fill();
}

function colorByPct(pct) {
  if (pct > 85) return 'red';
  if (pct > 70) return 'gold';
  return 'green';
}

function fmtGB(bytes) {
  if (bytes == null) return '—';
  return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}
function fmtMB(bytes) {
  if (bytes == null) return '—';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}
function fmtUptime(sec) {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ====== ORQUESTADOR AUTÓNOMO — panel ======
export async function renderOrquestadorPanel(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' }, [
    h('div', { class: 'flex items-center justify-between mb-6 flex-wrap gap-3' }, [
      h('div', {}, [
        h('h2', { class: 'font-display text-3xl font-bold' }, '🧠 Orquestador Autónomo'),
        h('p', { class: 'text-ink-300' }, 'CEO IA · piensa cada 30 min · low-risk auto · high-risk pide /aprobar-orq'),
      ]),
      h('div', { class: 'flex items-center gap-2' }, [
        h('button', { id: 'orch-think-btn', class: 'text-sm px-4 py-2 rounded bg-gold-500 hover:bg-gold-400 text-base-900 font-semibold' }, '🧠 Pensar ahora'),
        h('button', { class: 'text-xs text-gold-400 px-2 py-1 border border-base-700 rounded', onclick: () => renderOrquestadorPanel(root) }, '⟳ Refrescar'),
      ]),
    ]),
    h('div', { id: 'orch-status', class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-6' }),
    h('div', { id: 'orch-feed' }),
  ]);
  root.appendChild(wrap);
  // Load data
  try {
    const [statusRes, actionsRes] = await Promise.all([
      apiClient.get('/api/v1/orchestrator/status'),
      apiClient.get('/api/v1/orchestrator/actions?limit=30'),
    ]);
    const s = statusRes?.status || {};
    const statusBox = wrap.querySelector('#orch-status');
    const cards = [
      ['Último ciclo', s.lastCycleAt ? new Date(s.lastCycleAt).toLocaleString('es-ES') : '—', 'gold'],
      ['Decisiones hoy', String(s.todayCount ?? 0), 'emerald'],
      ['Pendientes', String(s.pendingCount ?? 0), 'amber'],
      ['Aprobadas/Rechazadas', `${s.approvedCount ?? 0}/${s.rejectedCount ?? 0}`, 'rose'],
    ];
    cards.forEach(([k, v, color]) => statusBox.appendChild(
      h('div', { class: 'rounded-lg border border-base-700 bg-base-900/60 p-4' }, [
        h('div', { class: 'text-xs uppercase tracking-wider text-ink-400 mb-1' }, k),
        h('div', { class: `text-2xl font-semibold text-${color}-400` }, v),
      ])
    ));
    const feed = wrap.querySelector('#orch-feed');
    const actions = actionsRes?.actions || [];
    if (!actions.length) {
      feed.appendChild(h('div', { class: 'text-center py-10 text-ink-400' }, 'Aún no hay decisiones. Pulsa "🧠 Pensar ahora".'));
    } else {
      const list = h('div', { class: 'space-y-2' });
      actions.forEach((a) => {
        const badge = ({ pending: 'amber', executed: 'emerald', approved: 'emerald', rejected: 'rose', failed: 'rose' })[a.status] || 'ink';
        const riskEmoji = ({ low: '🟢', medium: '🟡', high: '🔴' })[a.riskLevel] || '⚪';
        const row = h('div', { class: 'rounded border border-base-700 bg-base-900/40 p-3' }, [
          h('div', { class: 'flex items-center justify-between gap-3 flex-wrap' }, [
            h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
              h('span', {}, riskEmoji),
              h('strong', {}, a.action || '—'),
              h('span', { class: `text-xs px-2 py-0.5 rounded bg-${badge}-500/10 text-${badge}-300` }, a.status || '?'),
            ]),
            h('span', { class: 'text-xs text-ink-400' }, new Date(a.createdAt).toLocaleString('es-ES')),
          ]),
          a.why ? h('div', { class: 'text-sm text-ink-300 mt-1' }, a.why) : null,
          a.status === 'pending' ? h('div', { class: 'flex gap-2 mt-2' }, [
            h('button', { class: 'text-xs px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded', onclick: async () => {
              await apiClient.post(`/api/v1/orchestrator/actions/${a.id}/approve`, {});
              renderOrquestadorPanel(root);
            } }, '✅ Aprobar'),
            h('button', { class: 'text-xs px-3 py-1 bg-rose-500/20 text-rose-300 rounded', onclick: async () => {
              await apiClient.post(`/api/v1/orchestrator/actions/${a.id}/reject`, { reason: 'manual' });
              renderOrquestadorPanel(root);
            } }, '❌ Rechazar'),
          ]) : null,
        ].filter(Boolean));
        list.appendChild(row);
      });
      feed.appendChild(list);
    }
    const thinkBtn = wrap.querySelector('#orch-think-btn');
    thinkBtn.addEventListener('click', async () => {
      thinkBtn.disabled = true; thinkBtn.textContent = '🧠 Pensando...';
      try {
        await apiClient.post('/api/v1/orchestrator/think', {});
        renderOrquestadorPanel(root);
      } catch (e) {
        thinkBtn.textContent = '🧠 Pensar ahora';
        thinkBtn.disabled = false;
        alert('Error al pensar: ' + (e?.message || e));
      }
    });
  } catch (e) {
    wrap.querySelector('#orch-feed').appendChild(
      h('div', { class: 'text-rose-400 text-sm' }, 'Error cargando orquestador: ' + (e?.message || e))
    );
  }
}
